import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { DefinitionComponent } from './components/definition/definition.component';
import { MenuItem, MenuService } from '@services/menu.service';
import { MdsIcon } from '@mds/icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { MattersmapService } from './services/mattersmap.service';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { ComposerApiService } from '@services/composer-api.service';
import { filter, switchMap, tap } from 'rxjs/operators';
import { HistoryComponent } from './components/app-history/app-history.component';
import { ENTITY_TYPE_ICON_MAP, EntityType } from '@mds/common';
import { Observable, of, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';

export enum PageViews {
  HISTORY = 'history',
  DEFINITION = 'definition'
}

@Component({
  selector: 'app-mattermaps-edit',
  standalone: true,
  imports: [DefinitionComponent, HistoryComponent],
  templateUrl: './mattermaps-edit.component.html',
  styleUrls: ['./mattermaps-edit.component.scss']
})
export class MattermapsEditComponent implements OnInit {
  currentView = PageViews.DEFINITION;
  pageViews = PageViews;
  rid!: string;
  productName!: string;
  selectedTabName!: string;
  productImage: string;
  data: any;
  layer: string;
  entity: any;
  entityIcons: any[];
  isForecastScenario = false;
  private menuService = inject(MenuService);
  private composerApiService = inject(ComposerApiService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private mattersmapService = inject(MattersmapService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.route.parent.paramMap
      .pipe(
        tap((params: Params) => {
          this.layer = params.get('layer') || '';
          this.rid = params.get('rid');
          this.data = {
            rid: this.rid
          };
          this.entity = undefined;
          this.productImage = '';
          this.productName = '';
          this.entityIcons = [];
        }),
        switchMap((params) => this.composerApiService.getTileCards(this.rid)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.handleTileCardResponse.bind(this));

    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
      if (!action) {
        return;
      }
      this.parseMenuActions(action);
    });

    this.menuService.reload$
      .pipe(
        filter((reload) => !!reload),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.reloadData();
      });
  }

  canDeactivate(): Observable<boolean> {
    if (document.querySelector('#inlineFormSchema input.ng-touched.ng-valid')) {
      return this.discardChangesDialogMsg();
    }
    return of(true);
  }

  discardChangesDialogMsg() {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          header: 'Warning',
          text: 'It looks like you have been editing something. Please save; otherwise, your changes will be lost.',
          cancel_text: 'Cancel',
          delete_text: 'Continue'
        }
      })
      .afterClosed();
  }

  reloadSubscription: Subscription;

  private reloadData() {
    this.reloadSubscription?.unsubscribe();
    this.reloadSubscription = this.composerApiService
      .getTileCards(this.rid)
      .subscribe(this.handleTileCardResponse.bind(this));
  }

  handleTileCardResponse(results) {
    this.entity = results[0];
    this.productImage = this.entity.image ?? '';
    this.productName = this.entity.title;
    this.entityIcons = [{ key: getEntityTypeIcon(this.entity.stage) }];

    if (this.entity.is_forked && this.entity.stage !== 'makersite') {
      this.entityIcons.unshift({ key: MdsIcon.FORKED });
    }

    this.buildMenuActions(this.entity.actions || {});

    this.isForecastScenario = this.entity?.cardType === EntityType.SCENARIO_FORECAST;
  }

  buildMenuActions(actions: object): void {
    if (!this.entity) {
      return;
    }
    const centerActions = [
      {
        label: 'History',
        key: 'history',
        icon: MdsIcon.HISTORY,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Back To Editing',
        key: 'backToEdit',
        icon: MdsIcon.ARROW_BACKWARD_LIGHT,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Mapper',
        key: 'resume_import',
        icon: MdsIcon.BOM_IMPORT,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: this.entity.cardType === 'product'
      },
      {
        label: 'Datasheet',
        key: 'datasheet',
        icon: MdsIcon.COPY,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Enrich Products',
        key: 'enrich',
        icon: MdsIcon.COPY,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Open MCDA',
        key: 'open_mcda',
        icon: MdsIcon.COPY,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      }
    ];
    const rightActions = [
      {
        label: 'Save',
        key: 'save',
        icon: MdsIcon.SAVE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Save',
        key: 'save-grid',
        icon: MdsIcon.SAVE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Save',
        key: 'save_changes',
        icon: MdsIcon.SAVE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Merge',
        key: 'merge',
        icon: MdsIcon.FORK_OF,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: actions['merge']
      },
      {
        label: 'Re-calculate',
        key: 're_calculate',
        icon: MdsIcon.RECYCLED,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Pull request',
        key: 'pull',
        icon: MdsIcon.INITIATED,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: actions['pull']
      },
      {
        label: 'Deploy',
        key: 'deploy',
        icon: MdsIcon.FORKED,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: actions['deploy']
      },
      {
        label: 'Copy',
        key: 'duplicate',
        icon: MdsIcon.COPY,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Fork',
        key: 'fork',
        icon: MdsIcon.FORKED,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: actions['fork']
      },
      {
        label: 'Delete',
        key: 'delete',
        icon: MdsIcon.REMOVE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: actions['delete']
      },
      {
        label: 'Share',
        key: 'share',
        icon: MdsIcon.SHARE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Invite',
        key: 'invite',
        icon: MdsIcon.INVITE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Questionnaire',
        key: 'questionnaire',
        icon: MdsIcon.INVITE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      }
    ];

    this.menuService.setMenuConfig({
      activeApp: {
        mainIcon: {
          icon: MdsIcon.APP_PRODUCT_DATASHEET
        },
        rid: this.rid,
        name: 'Edit Mode',
        icons: [],
        subtitle: '',
        menuTrigger: 'selectApps',
        category: ''
      },
      activeEntity: {
        mainIcon: {
          icon: ENTITY_TYPE_ICON_MAP[this.entity.cardType],
          pathColors: ['#fff']
        },
        type: this.entity.cardType,
        rid: this.entity?.rid,
        name: this.entity?.title,
        icons: this.entityIcons,
        subtitle: this.entity.card_type_label
      },
      centerActions,
      rightActions
    });
  }

  getTabName(name: string) {
    this.selectedTabName = name;
  }

  private parseMenuActions(action: MenuItem) {
    const { key } = action;
    switch (key) {
      case 'resume_import':
        this.mattersmapService.resumeImport(this.rid);
        break;

      case 'datasheet':
        this.mattersmapService.openDatasheet(this.rid);
        break;

      case 'pull':
        this.mattersmapService.pull(this.rid);
        break;

      case 'merge':
      case 'deploy':
      case 'fork':
      case 'duplicate':
      case 'copy':
        this.mattersmapService.dataOperation(key, this.rid);
        break;

      case 'open_mcda':
        this.mattersmapService.openInMcda(this.rid);
        break;

      case 'delete':
        this.mattersmapService.deleteData(this.productName, this.rid, this.entity.cardType);
        break;

      case 'share':
        this.mattersmapService.shareDialogNew(this.layer, this.rid);
        break;

      case 'invite':
        this.mattersmapService.inviteDialogNew();
        break;

      case 're_calculate':
        this.mattersmapService.reCalculate(this.selectedTabName, this.rid);
        break;

      case 'enrich':
        this.mattersmapService.getEnrichProduct(this.rid);
        break;

      case 'history':
        this.currentView = PageViews.HISTORY;
        break;
      case 'backToEdit':
        this.currentView = PageViews.DEFINITION;
        this.menuService.updateMenuItem('backToEdit', { visible: false });
        this.menuService.updateMenuItem('history', { visible: true });
        break;
      default:
        break;
    }
  }
}
